import { v4 } from 'uuid';

import {
  DEFAULT_ALIGNMENT,
  DEFAULT_BEND,
  DEFAULT_BLEND_MODE,
  DEFAULT_DURATION,
  DEFAULT_HIDDEN,
  DEFAULT_LETTER_SPACING,
  DEFAULT_LINE_HEIGHT,
  DEFAULT_LOCKED,
  CRATE_DEFAULT_META,
  DEFAULT_OPACITY,
  DEFAULT_PERSPECTIVE_POINTS,
  DEFAULT_ROTATION,
  DEFAULT_SCALE_X,
  DEFAULT_SCALE_Y,
  DEFAULT_SHADOW,
  DEFAULT_START_TIME,
  DEFAULT_STROKE,
  DEFAULT_X,
  DEFAULT_Y,
  CREATE_DEFAULT_ANIMATIONS,
  CREATE_DEFAULT_CHILDREN,
  CREATE_DEFAULT_PATHS,
  SYSTEM_GROUP,
  SYSTEM_SHAPE,
  SYSTEM_TEXT,
  SYSTEM_IMAGE,
  SYSTEM_VIDEO,
  SYSTEM_AUDIO,
  CREATE_SYSTEM_RENDERER,
  DEFAULT_PATH,
  DEFAULT_REPEAT,
  DEFAULT_SPEED,
} from 'consts';

import {
  IReplayLayerType,
  IReplayTextLayer,
  IReplayVideoLayer,
  IReplayGroupLayer,
  IReplayShapeLayer,
  IReplayImageLayer,
  IReplayAudioLayer,
  IReplayLayerProperty,
  IReplayTextLayerCreate,
  IReplayAudioLayerCreate,
  IReplayVideoLayerCreate,
  IReplayGroupLayerCreate,
  IReplayShapeLayerCreate,
  IReplayImageLayerCreate,
  IReplayVisualLayer,
  IReplayVisualLayerCreate,
} from '../types';

const COEFFICIENT = 1000000;
const createReplayLayerProperty = <T>(
  value: T,
  locked = DEFAULT_LOCKED,
  meta = CRATE_DEFAULT_META(),
): IReplayLayerProperty<T> => {
  return {
    value:
      typeof value === 'number' ? ((Math.floor(value * COEFFICIENT) / COEFFICIENT) as T) : value,
    locked,
    meta,
  };
};

const createReplayGroupLayer = ({
  name,
  meta = CRATE_DEFAULT_META(),
  uuid = v4(),
  locked = DEFAULT_LOCKED,
  renderer = CREATE_SYSTEM_RENDERER(SYSTEM_GROUP),
  fallbackLayer,
  mediaType,
  props: {
    width,
    height,
    x = DEFAULT_X,
    y = DEFAULT_Y,
    scaleY = DEFAULT_SCALE_Y,
    hidden = DEFAULT_HIDDEN,
    scaleX = DEFAULT_SCALE_X,
    opacity = DEFAULT_OPACITY,
    rotation = DEFAULT_ROTATION,
    children = CREATE_DEFAULT_CHILDREN(),
    duration = DEFAULT_DURATION,
    startTime = DEFAULT_START_TIME,
    animations = CREATE_DEFAULT_ANIMATIONS(),
    blendMode = DEFAULT_BLEND_MODE,
  },
}: IReplayGroupLayerCreate): IReplayGroupLayer => {
  const groupLayer = {
    uuid,
    name,
    meta,
    locked,
    type: 'group' as IReplayLayerType,
    renderer,
    fallbackLayer,
    mediaType,
    props: {
      x: createReplayLayerProperty(x),
      y: createReplayLayerProperty(y),
      width: createReplayLayerProperty(width),
      height: createReplayLayerProperty(height),
      scaleY: createReplayLayerProperty(scaleY),
      scaleX: createReplayLayerProperty(scaleX),
      hidden: createReplayLayerProperty(hidden),
      opacity: createReplayLayerProperty(opacity),
      rotation: createReplayLayerProperty(rotation),
      children: createReplayLayerProperty(children),
      duration: createReplayLayerProperty(duration),
      startTime: createReplayLayerProperty(startTime),
      blendMode: createReplayLayerProperty(blendMode),
      animations: createReplayLayerProperty(animations) as IReplayGroupLayer['props']['animations'],
    },
  };

  return groupLayer;
};

const createReplayShapeLayer = ({
  name,
  meta = CRATE_DEFAULT_META(),
  uuid = v4(),
  locked = DEFAULT_LOCKED,
  renderer = CREATE_SYSTEM_RENDERER(SYSTEM_SHAPE),
  fallbackLayer,
  mediaType,
  props: {
    shape,
    width,
    height,
    x = DEFAULT_X,
    y = DEFAULT_Y,
    paths = CREATE_DEFAULT_PATHS(),
    scaleY = DEFAULT_SCALE_Y,
    stroke = DEFAULT_STROKE,
    scaleX = DEFAULT_SCALE_X,
    hidden = DEFAULT_HIDDEN,
    opacity = DEFAULT_OPACITY,
    shadow = DEFAULT_SHADOW,
    rotation = DEFAULT_ROTATION,
    duration = DEFAULT_DURATION,
    startTime = DEFAULT_START_TIME,
    blendMode = DEFAULT_BLEND_MODE,
    animations = CREATE_DEFAULT_ANIMATIONS(),
  },
}: IReplayShapeLayerCreate): IReplayShapeLayer => {
  const shapeLayer = {
    uuid,
    name,
    meta,
    locked,
    type: 'shape' as IReplayLayerType,
    renderer,
    fallbackLayer,
    mediaType,
    props: {
      x: createReplayLayerProperty(x),
      y: createReplayLayerProperty(y),
      width: createReplayLayerProperty(width),
      shape: createReplayLayerProperty(shape),
      paths: createReplayLayerProperty(paths),
      stroke: createReplayLayerProperty(stroke),
      scaleY: createReplayLayerProperty(scaleY),
      height: createReplayLayerProperty(height),
      scaleX: createReplayLayerProperty(scaleX),
      hidden: createReplayLayerProperty(hidden),
      shadow: createReplayLayerProperty(shadow),
      opacity: createReplayLayerProperty(opacity),
      rotation: createReplayLayerProperty(rotation),
      duration: createReplayLayerProperty(duration),
      startTime: createReplayLayerProperty(startTime),
      blendMode: createReplayLayerProperty(blendMode),
      animations: createReplayLayerProperty(animations) as IReplayShapeLayer['props']['animations'],
    },
  };
  return shapeLayer;
};

const createReplayTextLayer = ({
  name,
  meta = CRATE_DEFAULT_META(),
  uuid = v4(),
  locked = DEFAULT_LOCKED,
  renderer = CREATE_SYSTEM_RENDERER(SYSTEM_TEXT),
  fallbackLayer,
  mediaType,
  props: {
    font,
    width,
    ranges,
    y = DEFAULT_Y,
    x = DEFAULT_X,
    bend = DEFAULT_BEND,
    path = DEFAULT_PATH,
    stroke = DEFAULT_STROKE,
    scaleY = DEFAULT_SCALE_Y,
    scaleX = DEFAULT_SCALE_X,
    hidden = DEFAULT_HIDDEN,
    opacity = DEFAULT_OPACITY,
    shadow = DEFAULT_SHADOW,
    rotation = DEFAULT_ROTATION,
    duration = DEFAULT_DURATION,
    startTime = DEFAULT_START_TIME,
    alignment = DEFAULT_ALIGNMENT,
    blendMode = DEFAULT_BLEND_MODE,
    lineHeight = DEFAULT_LINE_HEIGHT,
    letterSpacing = DEFAULT_LETTER_SPACING,
    animations = CREATE_DEFAULT_ANIMATIONS(),
    perspectivePoints = DEFAULT_PERSPECTIVE_POINTS,
  },
}: IReplayTextLayerCreate): IReplayTextLayer => {
  const textLayer = {
    uuid,
    name,
    meta,
    locked,
    type: 'text' as IReplayLayerType,
    renderer,
    fallbackLayer,
    mediaType,
    props: {
      x: createReplayLayerProperty(x),
      y: createReplayLayerProperty(y),
      font: createReplayLayerProperty(font),
      bend: createReplayLayerProperty(bend),
      path: createReplayLayerProperty(path),
      width: createReplayLayerProperty(width),
      stroke: createReplayLayerProperty(stroke),
      scaleY: createReplayLayerProperty(scaleY),
      ranges: createReplayLayerProperty(ranges),
      scaleX: createReplayLayerProperty(scaleX),
      hidden: createReplayLayerProperty(hidden),
      shadow: createReplayLayerProperty(shadow),
      opacity: createReplayLayerProperty(opacity),
      rotation: createReplayLayerProperty(rotation),
      duration: createReplayLayerProperty(duration),
      startTime: createReplayLayerProperty(startTime),
      alignment: createReplayLayerProperty(alignment),
      blendMode: createReplayLayerProperty(blendMode),
      lineHeight: createReplayLayerProperty(lineHeight),
      letterSpacing: createReplayLayerProperty(letterSpacing),
      perspectivePoints: createReplayLayerProperty(perspectivePoints),
      animations: createReplayLayerProperty(animations) as IReplayTextLayer['props']['animations'],
    },
  };

  return textLayer;
};

const createReplayImageLayer = ({
  name,
  meta = CRATE_DEFAULT_META(),
  uuid = v4(),
  locked = DEFAULT_LOCKED,
  renderer = CREATE_SYSTEM_RENDERER(SYSTEM_IMAGE),
  fallbackLayer,
  mediaType,
  props: {
    image,
    y = DEFAULT_Y,
    x = DEFAULT_X,
    stroke = DEFAULT_STROKE,
    scaleY = DEFAULT_SCALE_Y,
    hidden = DEFAULT_HIDDEN,
    scaleX = DEFAULT_SCALE_X,
    opacity = DEFAULT_OPACITY,
    shadow = DEFAULT_SHADOW,
    rotation = DEFAULT_ROTATION,
    duration = DEFAULT_DURATION,
    startTime = DEFAULT_START_TIME,
    blendMode = DEFAULT_BLEND_MODE,
    animations = CREATE_DEFAULT_ANIMATIONS(),
  },
}: IReplayImageLayerCreate): IReplayImageLayer => {
  const imageLayer = {
    uuid,
    name,
    meta,
    locked,
    type: 'image' as IReplayLayerType,
    renderer,
    fallbackLayer,
    mediaType,
    props: {
      x: createReplayLayerProperty(x),
      y: createReplayLayerProperty(y),
      stroke: createReplayLayerProperty(stroke),
      image: createReplayLayerProperty(image),
      scaleY: createReplayLayerProperty(scaleY),
      scaleX: createReplayLayerProperty(scaleX),
      hidden: createReplayLayerProperty(hidden),
      shadow: createReplayLayerProperty(shadow),
      opacity: createReplayLayerProperty(opacity),
      rotation: createReplayLayerProperty(rotation),
      duration: createReplayLayerProperty(duration),
      startTime: createReplayLayerProperty(startTime),
      blendMode: createReplayLayerProperty(blendMode),
      animations: createReplayLayerProperty(animations) as IReplayShapeLayer['props']['animations'],
    },
  };
  return imageLayer;
};

const createReplayVideoLayer = ({
  name,
  uuid = v4(),
  meta = CRATE_DEFAULT_META(),
  locked = DEFAULT_LOCKED,
  renderer = CREATE_SYSTEM_RENDERER(SYSTEM_VIDEO),
  fallbackLayer,
  mediaType,
  props: {
    video,
    volume,
    trimEnd,
    trimStart,
    repeat = DEFAULT_REPEAT,
    speed = DEFAULT_SPEED,
    y = DEFAULT_Y,
    x = DEFAULT_X,
    hidden = DEFAULT_HIDDEN,
    scaleY = DEFAULT_SCALE_Y,
    scaleX = DEFAULT_SCALE_X,
    opacity = DEFAULT_OPACITY,
    rotation = DEFAULT_ROTATION,
    duration = DEFAULT_DURATION,
    startTime = DEFAULT_START_TIME,
    animations = CREATE_DEFAULT_ANIMATIONS(),
    blendMode = DEFAULT_BLEND_MODE,
  },
}: IReplayVideoLayerCreate): IReplayVideoLayer => {
  const videoLayer = {
    uuid,
    name,
    meta,
    locked,
    type: 'video' as IReplayLayerType,
    renderer,
    fallbackLayer,
    mediaType,
    props: {
      x: createReplayLayerProperty(x),
      y: createReplayLayerProperty(y),
      video: createReplayLayerProperty(video),
      scaleY: createReplayLayerProperty(scaleY),
      scaleX: createReplayLayerProperty(scaleX),
      hidden: createReplayLayerProperty(hidden),
      opacity: createReplayLayerProperty(opacity),
      volume: createReplayLayerProperty(volume),
      trimEnd: createReplayLayerProperty(trimEnd),
      rotation: createReplayLayerProperty(rotation),
      duration: createReplayLayerProperty(duration),
      trimStart: createReplayLayerProperty(trimStart),
      startTime: createReplayLayerProperty(startTime),
      blendMode: createReplayLayerProperty(blendMode),
      repeat: createReplayLayerProperty(repeat),
      speed: createReplayLayerProperty(speed),
      animations: createReplayLayerProperty(animations) as IReplayVideoLayer['props']['animations'],
    },
  };
  return videoLayer;
};

const createReplayAudioLayer = ({
  name,
  uuid = v4(),
  meta = CRATE_DEFAULT_META(),
  locked = DEFAULT_LOCKED,
  renderer = CREATE_SYSTEM_RENDERER(SYSTEM_AUDIO),
  fallbackLayer,
  mediaType,
  props: {
    audio,
    volume,
    trimEnd,
    trimStart,
    duration = DEFAULT_DURATION,
    startTime = DEFAULT_START_TIME,
    repeat = DEFAULT_REPEAT,
    speed = DEFAULT_SPEED,
    animations = CREATE_DEFAULT_ANIMATIONS(),
  },
}: IReplayAudioLayerCreate): IReplayAudioLayer => {
  const audioLayer = {
    uuid,
    name,
    meta,
    locked,
    type: 'audio' as IReplayLayerType,
    renderer,
    fallbackLayer,
    mediaType,
    props: {
      audio: createReplayLayerProperty(audio),
      volume: createReplayLayerProperty(volume),
      trimEnd: createReplayLayerProperty(trimEnd),
      duration: createReplayLayerProperty(duration),
      trimStart: createReplayLayerProperty(trimStart),
      startTime: createReplayLayerProperty(startTime),
      repeat: createReplayLayerProperty(repeat),
      speed: createReplayLayerProperty(speed),
      animations: createReplayLayerProperty(animations) as IReplayVideoLayer['props']['animations'],
    },
  };
  return audioLayer;
};

const createReplayVisualLayer = ({
  name,
  uuid = v4(),
  meta = CRATE_DEFAULT_META(),
  locked = DEFAULT_LOCKED,
  renderer,
  fallbackLayer,
  mediaType,
  props: {
    width,
    height,
    rendererData = {},
    y = DEFAULT_Y,
    x = DEFAULT_X,
    scaleY = DEFAULT_SCALE_Y,
    hidden = DEFAULT_HIDDEN,
    scaleX = DEFAULT_SCALE_X,
    opacity = DEFAULT_OPACITY,
    rotation = DEFAULT_ROTATION,
    duration = DEFAULT_DURATION,
    startTime = DEFAULT_START_TIME,
    blendMode = DEFAULT_BLEND_MODE,
    animations = CREATE_DEFAULT_ANIMATIONS(),
  },
}: IReplayVisualLayerCreate): IReplayVisualLayer => {
  const visualLayer = {
    uuid,
    name,
    meta,
    locked,
    type: 'visual' as IReplayLayerType,
    renderer,
    fallbackLayer,
    mediaType,
    props: {
      rendererData: createReplayLayerProperty(rendererData),
      width: createReplayLayerProperty(width),
      height: createReplayLayerProperty(height),
      y: createReplayLayerProperty(y),
      x: createReplayLayerProperty(x),
      scaleY: createReplayLayerProperty(scaleY),
      scaleX: createReplayLayerProperty(scaleX),
      hidden: createReplayLayerProperty(hidden),
      opacity: createReplayLayerProperty(opacity),
      rotation: createReplayLayerProperty(rotation),
      duration: createReplayLayerProperty(duration),
      startTime: createReplayLayerProperty(startTime),
      blendMode: createReplayLayerProperty(blendMode),
      animations: createReplayLayerProperty(animations) as IReplayVideoLayer['props']['animations'],
    },
  };
  return visualLayer;
};

export {
  createReplayVisualLayer,
  createReplayTextLayer,
  createReplayVideoLayer,
  createReplayAudioLayer,
  createReplayGroupLayer,
  createReplayImageLayer,
  createReplayShapeLayer,
  createReplayLayerProperty,
};
